/**
 * Owns the access token and every auth navigation. Nothing else writes the token or sends the user
 * to the login page / identity provider.
 *
 * Why it is shaped this way, the failure modes it prevents and the manual test fixtures:
 * `docs/session-auth-refactor.md`.
 */
import jwtDecode, { JwtPayload } from 'jwt-decode';
import { AuthenticationProvider, ValuationCountryCode } from 'src/modules/generated/api';
import {
  authRedirectAttemptKey,
  authenticationStorageKey,
  legacyValuationCountryStorageKey,
  legacyValuationTypeStorageKey,
} from 'src/modules/global-vars';

import type UserRole from 'src/types/UserRoles';

export type GwscoutJwtPayload = JwtPayload & {
  email: string;
  first_name: string;
  last_name: string;
  dealer_id: string[];
  user_id: number;
  scope: UserRole[];
  authentication_provider: string;
  valuation_country: ValuationCountryCode;
  is_allowed_to_login: boolean;
  country: string;
};

/**
 * `expired` is our own `exp` check failing; `rejected` is the backend refusing a token that looks
 * fine to us (key rotation, revocation, wrong environment). The two are independent.
 */
export type SessionState =
  | { kind: 'anonymous' }
  | { kind: 'active'; token: string; claims: GwscoutJwtPayload }
  | { kind: 'expired'; token: string; claims: GwscoutJwtPayload }
  | { kind: 'rejected'; token?: string; claims?: GwscoutJwtPayload };

/** `anonymous` carries no token, so this is the only safe way to compare two states' tokens. */
export const tokenOf = (state: SessionState): string | undefined => ('token' in state ? state.token : undefined);

/** Available for `expired` too, so the user's name and dealer stay displayable during a renewal. */
export const claimsOf = (state: SessionState): GwscoutJwtPayload | undefined =>
  'claims' in state ? state.claims : undefined;

/**
 * Not implied by `active`: the backend mints a valid, unexpired token for a disallowed dealer and
 * only sets `is_allowed_to_login: false`.
 */
export const canUseScout = (state: SessionState): boolean =>
  state.kind === 'active' && !!state.claims.is_allowed_to_login;

export const ACCESS_TOKEN_PARAM = 'access_token';

export const LOGIN_PATH = '/login';

/** Clock skew, so a token about to die is not sent on a request that would outlive it. */
export const EXPIRY_SKEW_MS = 60_000;

/** A corrupt or non-JWT token yields `undefined` rather than throwing. */
export const decodeClaims = (token: string | undefined): GwscoutJwtPayload | undefined => {
  if (!token) return undefined;

  try {
    return jwtDecode<GwscoutJwtPayload>(token);
  } catch {
    return undefined;
  }
};

export const buildAuthenticationUrl = (authBaseUrl: string, provider?: AuthenticationProvider) => {
  if (provider === AuthenticationProvider.Grp) return `${authBaseUrl}/oauth2/authorization/grp`;
  if (provider === AuthenticationProvider.PartnerNet) return `${authBaseUrl}/oauth2/authorization/partner-net`;
  return `${authBaseUrl}${LOGIN_PATH}`;
};

/** Pure. `rejected` is not reachable from here — the manager overlays it after a 401. */
export const readSessionState = (token: string | undefined, nowMs: number): SessionState => {
  if (!token) return { kind: 'anonymous' };

  const claims = decodeClaims(token);
  if (!claims) return { kind: 'anonymous' };

  // No `exp` counts as active: fail open and let the backend decide.
  const expired = claims.exp !== undefined && claims.exp * 1000 < nowMs + EXPIRY_SKEW_MS;

  return expired ? { kind: 'expired', token, claims } : { kind: 'active', token, claims };
};

export type SessionPorts = {
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** The one-shot SSO attempt marker: session-scoped, not install-scoped. */
  attemptStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Leaves the SPA — always a document replacement, never a router navigation. */
  navigate: (url: string) => void;
  location: () => { pathname: string; search: string; hash: string };
  /** Rewrites the address bar without reloading. */
  replaceUrl: (url: string) => void;
  now: () => number;
  /** `REACT_APP_ACCESS_TOKEN`, the dev-only fallback. */
  envToken?: string;
  /** Origin of the login page and identity provider (`''` when same-origin). */
  authBaseUrl: string;
};

/** Ports are injected so the whole module is testable without a DOM or a network. */
export const createSessionManager = (ports: SessionPorts) => {
  let rejected = false;

  /**
   * Stays true for the life of the document deliberately: 401s arrive in batches, and only the
   * first caller still sees the token needed to pick the SSO url.
   */
  let redirecting = false;

  const listeners = new Set<() => void>();

  const readToken = () => ports.storage.getItem(authenticationStorageKey) || ports.envToken || undefined;

  const computeState = (): SessionState => {
    const token = readToken();

    if (rejected) return { kind: 'rejected', token, claims: decodeClaims(token) };

    return readSessionState(token, ports.now());
  };

  let cached = computeState();

  /**
   * Recomputed per call so the React view and the request view cannot disagree, but returns the
   * cached object when nothing changed — `useSyncExternalStore` loops if the snapshot is not stable.
   */
  const getState = (): SessionState => {
    const next = computeState();

    if (next.kind === cached.kind && tokenOf(next) === tokenOf(cached)) return cached;

    cached = next;
    return next;
  };

  const notify = () => {
    listeners.forEach((listener) => listener());
  };

  const getAuthenticationUrl = (provider?: AuthenticationProvider) =>
    buildAuthenticationUrl(ports.authBaseUrl, provider);

  const clearToken = () => {
    ports.storage.removeItem(authenticationStorageKey);
  };

  const signIn = (token: string) => {
    ports.storage.setItem(authenticationStorageKey, token);
    ports.attemptStorage.removeItem(authRedirectAttemptKey);
    rejected = false;

    // "Reset valuation on every login", carried over verbatim. A no-op since valuation moved — see
    // the note on these keys in global-vars.
    ports.storage.removeItem(legacyValuationCountryStorageKey);
    ports.storage.removeItem(legacyValuationTypeStorageKey);

    notify();
  };

  const signOut = () => {
    clearToken();
    rejected = false;
    notify();
  };

  /**
   * Returns whether a navigation is in flight. Callers that suppress the error which triggered this
   * **must** check it: on `/login` nothing is started, and suppressing would hang them forever.
   */
  const beginReauthentication = (): boolean => {
    if (ports.location().pathname === LOGIN_PATH) return false;
    if (redirecting) return true;

    redirecting = true;

    // A second bounce would land in the same place, so fall back to the chooser, which is terminal.
    const alreadyTried = ports.attemptStorage.getItem(authRedirectAttemptKey) === 'true';
    ports.attemptStorage.setItem(authRedirectAttemptKey, 'true');

    // Read the provider off the dying token before dropping it — this is what makes the first
    // attempt seamless instead of a manual pick.
    const provider = alreadyTried
      ? undefined
      : (decodeClaims(readToken())?.authentication_provider as AuthenticationProvider | undefined);

    clearToken();
    ports.navigate(getAuthenticationUrl(provider));

    return true;
  };

  /** Authenticated, but this dealer may not use Scout. The backend serves `/forbidden`. */
  const goToForbidden = (): boolean => {
    if (redirecting) return true;

    redirecting = true;
    ports.navigate(`${ports.authBaseUrl}/forbidden`);

    return true;
  };

  /** GRP entry point: hand the signed target blob to the identity provider. */
  const beginTargetedAuthentication = (target: string): boolean => {
    if (redirecting) return true;

    redirecting = true;
    // Do not "tidy up" this encoding. `target` is already decoded once (URLSearchParams) and the
    // backend decodes exactly once more before checking the HMAC. One encode either way invalidates
    // the signature, and GRP then bounces to its retail start url — which looks nothing like an
    // auth failure.
    ports.navigate(`${getAuthenticationUrl(AuthenticationProvider.Grp)}?Target=${encodeURIComponent(target)}`);

    return true;
  };

  /** Returns whether a navigation started — the interceptor's cue for whether it may suppress. */
  const onUnauthorized = (): boolean => {
    rejected = true;

    const didNavigate = beginReauthentication();

    // No navigation means no reload is coming to clean up, so drop the token here instead.
    if (!didNavigate) clearToken();

    notify();

    return didNavigate;
  };

  /**
   * Re-arms seamless SSO. Keyed off any authenticated response rather than sign-in, because bounces
   * that never reach the `?access_token=` hand-off would otherwise leave the marker set.
   */
  const onAuthenticatedResponse = () => {
    if (ports.attemptStorage.getItem(authRedirectAttemptKey) === null) return;

    ports.attemptStorage.removeItem(authRedirectAttemptKey);
  };

  /**
   * Must run before the first render — see `session-bootstrap.ts` for why an effect cannot work.
   * Keeps the rest of the query string and the hash.
   */
  const captureTokenFromUrl = (): string | undefined => {
    const { pathname, search, hash } = ports.location();
    const params = new URLSearchParams(search);
    const token = params.get(ACCESS_TOKEN_PARAM);

    if (!token) return undefined;

    signIn(token);

    params.delete(ACCESS_TOKEN_PARAM);
    const remaining = params.toString();
    ports.replaceUrl(`${pathname}${remaining ? `?${remaining}` : ''}${hash}`);

    return token;
  };

  /**
   * An expired token is still sent: that reliably produces a 401 the interceptor can act on,
   * whereas omitting the header can yield a 302 or no decision at all.
   */
  const tokenForRequest = (): string => {
    const state = getState();

    return state.kind === 'active' || state.kind === 'expired' ? state.token : '';
  };

  /** For the few call sites that build an Authorization header by hand. */
  const currentToken = (): string | undefined => readToken();

  const subscribe = (listener: () => void) => {
    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  };

  return {
    beginReauthentication,
    beginTargetedAuthentication,
    captureTokenFromUrl,
    currentToken,
    getAuthenticationUrl,
    getState,
    goToForbidden,
    onAuthenticatedResponse,
    onUnauthorized,
    signIn,
    signOut,
    subscribe,
    tokenForRequest,
  };
};

export type SessionManager = ReturnType<typeof createSessionManager>;

export const session = createSessionManager({
  storage: window.localStorage,
  attemptStorage: window.sessionStorage,
  navigate: (url) => window.location.replace(url),
  location: () => window.location,
  replaceUrl: (url) => window.history.replaceState(null, '', url),
  now: () => Date.now(),
  // Storage first, env second: a token the app just dropped must not be resurrected by the env.
  envToken: process.env.REACT_APP_ACCESS_TOKEN,
  authBaseUrl: process.env.REACT_APP_API_URL || '',
});
